import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  flipCallCamera,
  shouldOfferCallCameraFlip,
} from '../utils/callControlUtils';
import { getVideoFacingModeFromStream } from '../../../../../shared/services/call/callMediaTrackUtils';
import { sendJuicyhouseCallEvent } from '../../../../../shared/utils/matrix/matrixReadUtils';
import { JUICYHOUSE_CALL_EVENT } from '../../../../../shared/utils/matrix/matrixRoomUtils';
import {
  CALL_EVENT_ACTION,
  CAMERA_FLIP_COOLDOWN_MS,
  JUICYHOUSE_CALL_EVENT_VERSION,
} from '../constant';

const isBackFacingCamera = (facingMode) => facingMode === 'environment';

/**
 * Phone-oriented video call: detect multiple cameras, listen for device changes,
 * and expose flip action wired to the Matrix MediaHandler.
 */
export function useCallCameraFlip({
  client,
  isInitialized,
  roomId,
  activeCall,
  localStream,
  isVoiceCallUi,
  isCallConnected,
  onFlipSuccess,
}) {
  const [callHasMultiCamera, setCallHasMultiCamera] = useState(false);
  const [cameraFlipBusy, setCameraFlipBusy] = useState(false);
  const [cameraFlipCooldown, setCameraFlipCooldown] = useState(false);
  const [isCameraFlipped, setIsCameraFlipped] = useState(false);
  const [isRemoteCameraFlipped, setIsRemoteCameraFlipped] = useState(false);
  const cameraFlipBusyRef = useRef(false);
  const cameraFlipCooldownRef = useRef(false);
  const flipCooldownTimerRef = useRef(null);
  const isCameraFlippedRef = useRef(false);
  isCameraFlippedRef.current = isCameraFlipped;

  useEffect(() => {
    setIsCameraFlipped(false);
    setIsRemoteCameraFlipped(false);
    if (flipCooldownTimerRef.current) {
      clearTimeout(flipCooldownTimerRef.current);
      flipCooldownTimerRef.current = null;
    }
    cameraFlipCooldownRef.current = false;
    setCameraFlipCooldown(false);
  }, [activeCall?.callId]);

  useEffect(() => {
    return () => {
      if (flipCooldownTimerRef.current) {
        clearTimeout(flipCooldownTimerRef.current);
        flipCooldownTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      setCallHasMultiCamera(false);
      return undefined;
    }
    if (
      isVoiceCallUi ||
      !shouldOfferCallCameraFlip() ||
      !isCallConnected ||
      !activeCall ||
      activeCall.callHasEnded?.()
    ) {
      setCallHasMultiCamera(false);
      return undefined;
    }
    (async () => {
      try {
        const list = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) return;
        const n = list.filter((d) => d.kind === 'videoinput' && d.deviceId).length;
        setCallHasMultiCamera(n >= 2);
      } catch {
        if (!cancelled) setCallHasMultiCamera(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isVoiceCallUi, isCallConnected, activeCall, activeCall?.callId, localStream]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.addEventListener) {
      return undefined;
    }
    const refresh = () => {
      if (
        isVoiceCallUi ||
        !shouldOfferCallCameraFlip() ||
        !isCallConnected ||
        !activeCall ||
        activeCall.callHasEnded?.()
      ) {
        setCallHasMultiCamera(false);
        return;
      }
      navigator.mediaDevices
        .enumerateDevices()
        .then((list) => {
          const n = list.filter((d) => d.kind === 'videoinput' && d.deviceId).length;
          setCallHasMultiCamera(n >= 2);
        })
        .catch(() => setCallHasMultiCamera(false));
    };
    navigator.mediaDevices.addEventListener('devicechange', refresh);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', refresh);
    };
  }, [isVoiceCallUi, isCallConnected, activeCall, activeCall?.callId]);

  const applyLocalFacingMode = useCallback((facingMode) => {
    if (facingMode === 'environment') setIsCameraFlipped(true);
    else if (facingMode === 'user') setIsCameraFlipped(false);
  }, []);

  // Keep local preview mirror in sync with the live track, not a delayed toggle.
  // Android often omits facingMode for a few hundred ms after replaceTrack.
  useLayoutEffect(() => {
    if (!localStream?.addEventListener) {
      applyLocalFacingMode(getVideoFacingModeFromStream(localStream));
      return undefined;
    }
    let retryTimers = [];
    let videoTrack = null;
    const clearRetries = () => {
      retryTimers.forEach(clearTimeout);
      retryTimers = [];
    };
    const syncFromStream = () => {
      applyLocalFacingMode(getVideoFacingModeFromStream(localStream));
    };
    const unbindTrack = () => {
      videoTrack?.removeEventListener?.('unmute', syncWithRetries);
      videoTrack?.removeEventListener?.('ended', syncWithRetries);
      videoTrack = null;
    };
    const bindTrack = () => {
      unbindTrack();
      videoTrack =
        localStream.getVideoTracks?.()?.find((track) => track?.readyState === 'live') ??
        localStream.getVideoTracks?.()?.[0] ??
        null;
      videoTrack?.addEventListener?.('unmute', syncWithRetries);
      videoTrack?.addEventListener?.('ended', syncWithRetries);
    };
    const syncWithRetries = () => {
      syncFromStream();
      bindTrack();
      clearRetries();
      retryTimers = [50, 150, 400].map((ms) => setTimeout(syncFromStream, ms));
    };
    syncWithRetries();
    localStream.addEventListener('addtrack', syncWithRetries);
    localStream.addEventListener('removetrack', syncWithRetries);
    return () => {
      clearRetries();
      unbindTrack();
      localStream.removeEventListener('addtrack', syncWithRetries);
      localStream.removeEventListener('removetrack', syncWithRetries);
    };
  }, [localStream, applyLocalFacingMode]);

  const sendCameraFlipState = useCallback((flipped, facingMode) => {
    if (!client || !isInitialized || !roomId) return;
    void sendJuicyhouseCallEvent(client, {
      isInitialized,
      roomId,
      content: {
        version: JUICYHOUSE_CALL_EVENT_VERSION,
        action: CALL_EVENT_ACTION.CAMERA_FLIP,
        callId: activeCall?.callId ?? null,
        flipped,
        facingMode: facingMode || (flipped ? 'environment' : 'user'),
        senderUserId: client?.getUserId?.() ?? null,
        sentAtMs: Date.now(),
      },
    });
  }, [client, isInitialized, roomId, activeCall?.callId]);

  const handleFlipCamera = useCallback(async () => {
    if (!client || cameraFlipBusyRef.current || cameraFlipCooldownRef.current) {
      return { ok: false, reason: 'busy-or-no-client' };
    }
    cameraFlipBusyRef.current = true;
    setCameraFlipBusy(true);
    const nextFlipped = !isCameraFlippedRef.current;
    const nextFacing = nextFlipped ? 'environment' : 'user';
    // Tell the peer to apply mirror CSS before new frames arrive (Android GUM is slow).
    sendCameraFlipState(nextFlipped, nextFacing);
    try {
      const result = await flipCallCamera({ client, activeCall, localStream });
      if (result?.ok) {
        const appliedFacing = result.facingMode || nextFacing;
        const actualFlipped = result.facingMode
          ? isBackFacingCamera(appliedFacing)
          : nextFlipped;
        applyLocalFacingMode(appliedFacing);
        if (Boolean(actualFlipped) !== Boolean(nextFlipped)) {
          sendCameraFlipState(actualFlipped, appliedFacing);
        }
        if (typeof onFlipSuccess === 'function') onFlipSuccess();
        if (flipCooldownTimerRef.current) clearTimeout(flipCooldownTimerRef.current);
        cameraFlipCooldownRef.current = true;
        setCameraFlipCooldown(true);
        flipCooldownTimerRef.current = setTimeout(() => {
          cameraFlipCooldownRef.current = false;
          setCameraFlipCooldown(false);
          flipCooldownTimerRef.current = null;
        }, CAMERA_FLIP_COOLDOWN_MS);
      } else {
        sendCameraFlipState(isCameraFlippedRef.current);
      }
      return result;
    } finally {
      cameraFlipBusyRef.current = false;
      setCameraFlipBusy(false);
    }
  }, [client, activeCall, localStream, applyLocalFacingMode, sendCameraFlipState, onFlipSuccess]);

  useEffect(() => {
    if (!client || !isInitialized || !roomId) return undefined;
    const localUserId = client.getUserId?.() ?? null;
    const onTimeline = (event, room, toStartOfTimeline) => {
      if (toStartOfTimeline) return;
      if (!event || room?.roomId !== roomId) return;
      if (event.getType?.() !== JUICYHOUSE_CALL_EVENT) return;
      const content = event.getContent?.() ?? {};
      if (content.version !== JUICYHOUSE_CALL_EVENT_VERSION) return;
      if (content.action !== CALL_EVENT_ACTION.CAMERA_FLIP) return;
      if (content.senderUserId && localUserId && content.senderUserId === localUserId) return;
      const currentCallId = activeCall?.callId ?? null;
      if (currentCallId && content.callId && String(content.callId) !== String(currentCallId)) return;
      if (content.facingMode === 'environment' || content.facingMode === 'user') {
        setIsRemoteCameraFlipped(isBackFacingCamera(content.facingMode));
        return;
      }
      setIsRemoteCameraFlipped(Boolean(content.flipped));
    };
    client.on?.('Room.timeline', onTimeline);
    return () => {
      client.removeListener?.('Room.timeline', onTimeline);
    };
  }, [client, isInitialized, roomId, activeCall?.callId]);

  const showCameraFlipControl =
    !isVoiceCallUi && isCallConnected && shouldOfferCallCameraFlip() && callHasMultiCamera;

  const cameraFlipDisabled = cameraFlipBusy || cameraFlipCooldown;

  return {
    showCameraFlipControl,
    cameraFlipBusy,
    cameraFlipDisabled,
    isCameraFlipped,
    isRemoteCameraFlipped,
    handleFlipCamera,
  };
}
